import { useEffect, useState } from 'react';
import WatchlistDashboardService, { ActivationSummary } from '../WatchlistDashboardService';
import { OutcomeTab } from './StrategicSection';
import ActivationCard from './ActivationCard';

/**
 * Layer 3 — Activation Watchlists section. One card per enabled outcome (same dynamic list as
 * Layer 2, driven by institute.commitment_paths). Each card's stage counts come from the
 * per-outcome activation summary endpoint.
 */
const ActivationSection = ({
    outcomes,
    instituteId,
    filterInstituteId,
}: {
    outcomes: OutcomeTab[];
    instituteId: string | number;
    filterInstituteId?: string | null;
}) => {
    const [cards, setCards] = useState<ActivationSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (outcomes.length === 0) return;
        setLoading(true);
        setError(null);
        Promise.all(outcomes.map((o) => WatchlistDashboardService.getActivationSummary(o.key, instituteId, filterInstituteId)))
            .then((results) => setCards(results))
            .catch((err) => setError(err?.message || 'Failed to load activation watchlists.'))
            .finally(() => setLoading(false));
    }, [outcomes, instituteId, filterInstituteId]);

    const totalInExecution = cards.reduce((sum, c) => sum + (c.total_in_outcome || 0), 0);

    return (
        <div className="col-12">
            {/* Layer 3 header (mockup lines 344-350) */}
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-3 mt-4 px-3 py-3 rounded-3 bg-success bg-opacity-10 border-4 border-start border-success border-opacity-100">
                <span className="font-10 font-600 text-uppercase px-2 py-1 rounded-pill bg-success text-white">Layer 3</span>
                <span className="font-18 font-600 text-dark">Activation Watchlists</span>
                <span className="flex-grow-1 font-13 text-secondary-emphasis">
                    Drive the result — the only pipelines with stages, structured engagement, and measurable outcomes
                </span>
                {!loading && !error && (
                    <span className="font-12 font-600 py-1 px-3 rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-10">
                        {totalInExecution} alumni in execution
                    </span>
                )}
            </div>

            {loading && <div className="font-14 text-secondary-emphasis py-2">Loading activation watchlists…</div>}
            {error && !loading && (
                <div className="alert alert-danger font-13" role="alert">
                    {error}
                </div>
            )}
            {!loading && !error && (
                <div className="row g-3 row-cols-1 row-cols-md-2 mb-4">
                    {cards.map((card) => (
                        <ActivationCard key={card.outcome} summary={card} instituteId={instituteId} filterInstituteId={filterInstituteId} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ActivationSection;
